import { matchPath } from "react-router-dom";

import { canPayBrebQr } from "@/app/auth/canPayBrebQr";
import { canReachPayoutBatches } from "@/app/auth/canReachPayoutBatches";
import type { Permission } from "@/shared/types/permission.types";
import { UserRole, UserType, type UserEntity } from "@/shared/types/user.entity";

import {
  NAVIGATION_REGISTRY,
  NAV_MENU_GROUPS,
  SECTION_IDS,
  type NavContext,
  type NavEntry,
  type NavMenuGroup,
  type SectionId,
} from "./navigation.registry";

/**
 * Pure resolution of {@link NAVIGATION_REGISTRY} against a user. No React, no Redux, no HTTP — which is what
 * makes the whole authorization surface testable in one file.
 *
 * Two distinct questions, deliberately not the same predicate:
 *
 * - {@link isEntryVisible} — "does this belong in the sidebar?"
 * - {@link isEntryRoutable} — "may this user open it?"
 *
 * They differ by the two axes that curate rather than authorize:
 *
 * - `internalOnlyMenu` — the API would serve the request, so the flag hides the door without locking it. That
 *   is what the dashboard does today for Swaps and Users, and what an org member reaching either by deep link
 *   still relies on.
 * - `enabled` — a feature flag, and `pocketsVisible` resolves asynchronously: gating the ROUTE on it would deny
 *   the page for the first render, before the flag arrives. It never gated routing before either.
 *
 * Every authorization axis — tenancy, the organization allow-list, RBAC — applies to both, so a menu item and
 * its route cannot disagree.
 */

export interface ResolvedSection {
  id: SectionId;
  entries: NavEntry[];
}

/** Build the lookup set `can()`-style checks and the resolver share. Tolerates a missing `scopes`. */
export function buildPermissionSet(scopes: readonly Permission[] | undefined | null): Set<Permission> {
  return new Set(scopes ?? []);
}

/**
 * Derive the resolver's input from the session user. Returns `null` when there is no user, so callers render
 * nothing rather than guessing.
 */
export function buildNavContext(
  user: UserEntity | null | undefined,
  options: {
    pocketsVisible: boolean;
    walletTopUpsAllowed?: boolean;
    metricsAllowed?: boolean;
    cryptoEnabled?: boolean;
  },
): NavContext | null {
  if (!user) return null;
  const allowedModules = user.organization?.allowed_menu_modules;
  return {
    userType: user.user_type,
    role: user.role,
    permissions: buildPermissionSet(user.scopes),
    orgAllowedSections: allowedModules && allowedModules.length > 0 ? new Set<string>(allowedModules) : null,
    pocketsVisible: options.pocketsVisible,
    payoutBatchesEnabled: canReachPayoutBatches(user),
    walletTopUpsAllowed: options.walletTopUpsAllowed === true,
    metricsAllowed: options.metricsAllowed === true,
    cryptoEnabled: user.user_type === UserType.INTERNAL || options.cryptoEnabled === true,
    brebQrPaymentsEnabled: canPayBrebQr(user),
  };
}

/**
 * The axes shared by menu visibility and route access, in the order that makes each failure cheap to reason
 * about. The INTERNAL+ADMIN bypass sits AFTER tenancy and the org allow-list, and BEFORE RBAC: it mirrors the
 * production guard (`master` grants an internal admin everything before consulting permissions), so a release
 * that reaches production without the RBAC backend still shows admins their full menu. It must never override
 * tenancy or a feature flag.
 */
function passesSharedAxes(entry: NavEntry, ctx: NavContext): boolean {
  // 1. Tenancy — mirrors @JwtAuth({ userTypes }) on the endpoints behind the view.
  if (entry.userTypes && !entry.userTypes.includes(ctx.userType)) return false;

  // 2. Organization module allow-list. Applies to routes too, so an org limited to ["home"] cannot
  //    reach a management view by typing its URL.
  if (entry.section && ctx.orgAllowedSections && !ctx.orgAllowedSections.has(entry.section)) {
    return false;
  }

  // 3. Production-guard bypass. See the note above — not technical debt.
  if (ctx.userType === UserType.INTERNAL && ctx.role === UserRole.ADMIN) return true;

  // 4. RBAC, ANY-of, DEFAULT DENY.
  if (entry.requires?.length) {
    return entry.requires.some((permission) => ctx.permissions.has(permission));
  }
  return true;
}

/** Should this entry appear in the sidebar / footer? */
export function isEntryVisible(entry: NavEntry, ctx: NavContext): boolean {
  if (entry.internalOnlyMenu && ctx.userType !== UserType.INTERNAL) return false;
  if (entry.enabled && !entry.enabled(ctx)) return false;
  return passesSharedAxes(entry, ctx);
}

/** May this user open this destination? Product curation does not apply here — see the file note. */
export function isEntryRoutable(entry: NavEntry, ctx: NavContext): boolean {
  return passesSharedAxes(entry, ctx);
}

/** Sidebar entries grouped by section, in registry order. Empty sections are dropped. */
export function resolveSections(
  ctx: NavContext,
  registry: readonly NavEntry[] = NAVIGATION_REGISTRY,
): ResolvedSection[] {
  return SECTION_IDS.map((id) => ({
    id,
    entries: registry.filter(
      (entry) => entry.placement === "sidebar" && entry.section === id && isEntryVisible(entry, ctx),
    ),
  })).filter((section) => section.entries.length > 0);
}

/**
 * One row of a rendered section: either an entry, or a collapsible group holding several of them.
 *
 * Presentation, deliberately separate from {@link resolveSections}, which answers the different question of
 * what a user MAY SEE. Folding runs after, on the already-authorized list, so a group can never add or remove
 * access — it only decides where a row is drawn.
 */
export type ResolvedNavNode =
  | { kind: "entry"; entry: NavEntry }
  | { kind: "group"; group: NavMenuGroup; entries: NavEntry[] };

/**
 * Fold a section's visible entries into its collapsible groups.
 *
 * Three rules, each of them a state the UI would otherwise get wrong:
 *
 * - **A group with no visible member is not emitted.** A disclosure that opens onto nothing is worse than no
 *   disclosure; the same discipline `resolveSections` applies when it drops an empty section.
 * - **A group with exactly ONE visible member renders as that member.** Hiding a single destination behind a
 *   click buys nothing. Reachable as soon as the members' requirements diverge — which is precisely why they
 *   are declared as separate entries.
 * - **A group takes the position of its first member**, so registry order still governs the section.
 */
export function groupSectionEntries(
  entries: readonly NavEntry[],
  groups: readonly NavMenuGroup[] = NAV_MENU_GROUPS,
): ResolvedNavNode[] {
  const nodes: ResolvedNavNode[] = [];
  const emitted = new Set<string>();

  for (const entry of entries) {
    const group = entry.menuGroup ? groups.find((candidate) => candidate.id === entry.menuGroup) : undefined;

    // An entry naming a group nobody declared still has to render. Silently dropping a destination the user
    // is authorized for would be a far worse failure than showing it unfolded.
    if (!group) {
      nodes.push({ kind: "entry", entry });
      continue;
    }

    if (emitted.has(group.id)) continue;
    emitted.add(group.id);

    const members = entries.filter((candidate) => candidate.menuGroup === group.id);
    nodes.push(
      members.length === 1
        ? { kind: "entry", entry: members[0] }
        : { kind: "group", group, entries: members },
    );
  }

  return nodes;
}

/** Footer entries (settings, news), in registry order. */
export function resolveFooterEntries(
  ctx: NavContext,
  registry: readonly NavEntry[] = NAVIGATION_REGISTRY,
): NavEntry[] {
  return registry.filter((entry) => entry.placement === "footer" && isEntryVisible(entry, ctx));
}

/** Look an entry up by its unique `to`. */
export function findEntryByTo(
  to: string,
  registry: readonly NavEntry[] = NAVIGATION_REGISTRY,
): NavEntry | undefined {
  return registry.find((entry) => entry.to === to);
}

/**
 * The entries a mounted route serves — every entry declaring that route pattern as its `path`.
 *
 * A wildcard route serves several surfaces: `cards/*` renders `CardsNavigation`, which routes to cards,
 * holders and pockets — three entries with three different requirements. Most patterns are declared by
 * exactly one entry, in which case the group is that entry.
 */
export function findRouteGroup(
  path: string,
  registry: readonly NavEntry[] = NAVIGATION_REGISTRY,
): NavEntry[] {
  return registry.filter((entry) => entry.path === path);
}

/**
 * May this user open the route mounted at `path`? ANY member of the group is enough.
 *
 * ANY, not ALL: requiring every surface would lock a user out of the whole cards module for lacking
 * `wallets:read`, when they can perfectly well use cards and holders.
 *
 * This answers only "may the route render". It is NOT permission to open every surface inside it: each
 * sub-route is guarded against its own entry with `isEntryRoutable`, and the module's index redirect resolves
 * to the first surface the user can reach. Skipping that pairing would turn ANY into a hole — a user with
 * only `identities:read` would enter `cards/*`, be redirected to Cards, and get a 401 page.
 *
 * An unknown pattern yields an empty group and therefore a denial: a route wired without a registry entry
 * fails closed instead of silently opening.
 */
export function isRouteGroupAccessible(
  path: string,
  ctx: NavContext,
  registry: readonly NavEntry[] = NAVIGATION_REGISTRY,
): boolean {
  const group = findRouteGroup(path, registry);
  if (group.length === 0) return false;
  return group.some((entry) => isEntryRoutable(entry, ctx));
}

/**
 * Which menu item owns the current URL — exactly one, or none.
 *
 * This lives here, next to the registry, and NOT in the sidebar component, for three reasons:
 *
 * 1. **The winner is global, not per section.** The sidebar renders one component per section, so a maximum
 *    computed inside it is a per-section maximum and two sections could each elect a highlighted row.
 * 2. **It is testable without a Router** — the whole point of this file.
 * 3. **The design system stays dumb.** `ColocaNavMain` receives the answer as a string and compares it; it never
 *    learns what a registry is.
 *
 * The rule is MOST SPECIFIC WINS, measured in matched path SEGMENTS. A boolean prefix match cannot work:
 * `matchPath({ end: false })` reports `/app/crypto-swaps` as matching `/app/crypto-swaps/history`, so both
 * rows highlighted at once. Ranking by depth is self-maintaining — add a deeper sibling and the shallower one
 * stops claiming its URLs, with nothing to remember per entry.
 *
 * Segments, not characters: `pathnameBase.length` would make a long label beat a deeper route.
 */

/** How deep `pattern` matches `pathname`, in path segments. `-1` when it does not match at all. */
function matchDepth(pattern: string, pathname: string): number {
  // `end: false` so a parent still matches its own children; `matchPath` is segment-aware, which is why
  // `/app/accounts-wallets` correctly does NOT match `/app/accounts-wallets-tracking`. Never reach for
  // `startsWith` here — it would.
  const match = matchPath({ path: pattern, end: false }, pathname);
  if (!match) return -1;
  return match.pathnameBase.split("/").filter(Boolean).length;
}

/**
 * The `to` of the entry that should render active for `pathname`, or `null` when no menu item owns it.
 *
 * Ties are broken by registry order, so the answer is deterministic; the test asserts that no entry's own
 * destination is ever tied, which is the case that would actually be ambiguous.
 */
export function resolveActiveNavTarget(
  pathname: string,
  registry: readonly NavEntry[] = NAVIGATION_REGISTRY,
): string | null {
  let winner: string | null = null;
  let best = 0;

  for (const entry of registry) {
    // A `route` entry draws no row, so it can never be the answer — it delegates to whichever item claims
    // it through `activeFor`.
    if (entry.placement === "route") continue;

    for (const pattern of [entry.to, ...(entry.activeFor ?? [])]) {
      const depth = matchDepth(pattern, pathname);
      if (depth > best) {
        best = depth;
        winner = entry.to;
      }
    }
  }

  return winner;
}

/**
 * Where to send a user who has not asked for anything in particular: after login, and from the `/app/*`
 * catch-all.
 *
 * `/app/wallets` wins whenever it is visible — it is today's landing for everyone and the home of the product
 * — and otherwise the first visible sidebar entry takes over. `null` means the user can see nothing at all,
 * which the caller must render as an explicit "no access yet" state rather than bouncing them between guarded
 * routes.
 *
 * This replaces the role-keyed table that sent any `operator` to `/app/news`: that lookup ignored
 * `user_type`, so an organization member invited as an operator landed on the INTERNAL news page.
 */
export const PREFERRED_LANDING_PATH = "/app/wallets";

export function resolveLandingPath(
  ctx: NavContext,
  registry: readonly NavEntry[] = NAVIGATION_REGISTRY,
): string | null {
  // `isEntryVisible`, not `isEntryRoutable`: landing somewhere the sidebar does not show would drop the
  // user on a page with no corresponding menu item. Both fallbacks use the same predicate.
  const preferred = findEntryByTo(PREFERRED_LANDING_PATH, registry);
  if (preferred && isEntryVisible(preferred, ctx)) return preferred.to;

  const firstVisible = registry.find((entry) => entry.placement === "sidebar" && isEntryVisible(entry, ctx));
  return firstVisible?.to ?? null;
}
